import { useMutation, useQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'

export type ImiDeclarationItem = {
  declarationId: string
  driverLatinFullName: string
  declarationStartDate?: string
  declarationEndDate?: string
  declarationStatus?: string
  declarationLastUpdate?: string
  declarationPostingCountry?: string
}

type GetDeclarationsParams = {
  status?: string
  dateFrom?: string
  dateTo?: string
  driverNames?: string[]
}

type CreateImiDeclarationPayload = {
  importId: number
  options: unknown
  taskId: string
}

const IMI_SUPPORTED_LANGUAGES = new Set([
  'bg', 'cs', 'da', 'de', 'et', 'el', 'en', 'es', 'fr', 'fi', 'ga', 'hr',
  'hu', 'is', 'it', 'lv', 'lt', 'mt', 'nl', 'no', 'pl', 'pt', 'ro', 'sk',
  'sl', 'sv',
])

const IMI_LANGUAGE_FALLBACKS: Record<string, string> = {
  ua: 'en',
  ru: 'en',
}

const resolveImiLanguage = (language?: string) => {
  const normalized = language?.trim().toLowerCase() || 'pl'
  const mapped = IMI_LANGUAGE_FALLBACKS[normalized] || normalized

  if (IMI_SUPPORTED_LANGUAGES.has(mapped)) {
    return mapped
  }

  return 'en'
}

const getFilenameFromDisposition = (disposition?: string, fallback?: string) => {
  if (!disposition) {
    return fallback || 'imi-declaration.pdf'
  }

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1])
  }

  const basicMatch = disposition.match(/filename="?([^";]+)"?/i)
  if (basicMatch?.[1]) {
    return basicMatch[1]
  }

  return fallback || 'imi-declaration.pdf'
}

const sanitizeFilename = (value: string) =>
  value
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

// Filename for a downloaded declaration PDF: "imię nazwisko, miejsce, od-do".
export const buildImiPdfFilename = (item: {
  declarationId: string
  driverLatinFullName?: string
  declarationPostingCountry?: string
  declarationStartDate?: string
  declarationEndDate?: string
}) => {
  const dateRange = [item.declarationStartDate, item.declarationEndDate]
    .filter(Boolean)
    .join(' - ')
  const base = sanitizeFilename(
    [item.driverLatinFullName, item.declarationPostingCountry, dateRange]
      .filter(Boolean)
      .join(', '),
  )
  return `${(base || `imi-declaration-${item.declarationId}`).toUpperCase()}.pdf`
}

export const fetchImiDeclarationPdfBytes = async (
  companyId: string,
  declarationId: string,
  language = 'pl',
): Promise<Uint8Array> => {
  const blobResponse = await axiosInstance.get(
    `api/${companyId}/export/imi/declarations/${declarationId}/pdf`,
    { params: { language }, responseType: 'arraybuffer' },
  )

  const contentType = (blobResponse.headers['content-type'] || '').toString()
  if (contentType.includes('application/json')) {
    const errorText = new TextDecoder().decode(blobResponse.data)
    throw new Error(errorText || 'IMI declaration print generation failed')
  }

  return new Uint8Array(blobResponse.data)
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Zrzuca migawkę po stronie serwera - inaczej "Odśwież" przerysowałby tylko to,
// co backend trzyma w cache. Samo pobranie listy odbuduje ją z IMI.
export const invalidateImiDeclarationsSnapshot = (companyId: string) =>
  axiosInstance.post(`api/${companyId}/export/imi/declarations/refresh`)

export function useImiDeclarations(companyId: string, params: GetDeclarationsParams) {
  return useQuery({
    queryKey: ['imi-declarations', companyId, params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`api/${companyId}/export/imi/declarations`, {
        params: {
          status: params.status || undefined,
          dateFrom: params.dateFrom || undefined,
          dateTo: params.dateTo || undefined,
          driverNames: params.driverNames?.length
            ? params.driverNames.join('|')
            : undefined,
        },
      })

      return {
        items: (data?.items ?? []) as ImiDeclarationItem[],
        totalCount: (data?.totalCount ?? 0) as number,
      }
    },
    enabled: !!companyId,
  })
}

export function usePrintImiDeclaration(companyId: string) {
  return useMutation({
    mutationFn: async ({
      declarationId,
      item,
      language = 'pl',
    }: {
      declarationId: string
      item?: ImiDeclarationItem
      language?: string
    }) => {
      // Fetch the PDF bytes through the backend (it resolves the IMI presigned URL
      // server-side), so we control the downloaded filename: "imię nazwisko, miejsce, od-do".
      const blobResponse = await axiosInstance.get(
        `api/${companyId}/export/imi/declarations/${declarationId}/pdf`,
        { params: { language }, responseType: 'blob' },
      )

      const contentType = (blobResponse.headers['content-type'] || '').toString()
      if (contentType.includes('application/json')) {
        const errorText = await blobResponse.data.text()
        throw new Error(errorText || 'IMI declaration print generation failed')
      }

      const filename = item
        ? buildImiPdfFilename(item)
        : getFilenameFromDisposition(
            blobResponse.headers['content-disposition'],
            `IMI-DECLARATION-${declarationId}.PDF`,
          ).toUpperCase()

      downloadBlob(blobResponse.data, filename)
      return true
    },
  })
}

export function useEmailImiDeclaration(companyId: string, language = 'pl') {
  return useMutation({
    mutationFn: async ({ declarationId }: { declarationId: string }) => {
      const { data } = await axiosInstance.post(
        `api/${companyId}/export/imi/declarations/${declarationId}/email`,
        { language: resolveImiLanguage(language) },
      )

      return data
    },
  })
}

export function useWithdrawImiDeclaration(companyId: string) {
  return useMutation({
    mutationFn: async ({ declarationId }: { declarationId: string }) => {
      const { data } = await axiosInstance.post(
        `api/${companyId}/export/imi/declarations/${declarationId}/withdraw`,
      )

      return data
    },
  })
}

export function useSubmitImiDeclaration(companyId: string) {
  return useMutation({
    mutationFn: async ({ declarationId }: { declarationId: string }) => {
      const { data } = await axiosInstance.post(
        `api/${companyId}/export/imi/declarations/${declarationId}/submit`,
      )

      return data
    },
  })
}

export function useCreateImiDeclaration(companyId: string) {
  return useMutation({
    mutationFn: async ({ importId, options, taskId }: CreateImiDeclarationPayload) => {
      const { data } = await axiosInstance.post(`api/${companyId}/export/imiExport`, {
        options: JSON.stringify(options),
        importId,
        taskId,
      })

      return data
    },
  })
}
